// ── 주문 등록/수정 모달 ────────────────────────────────────────────────────────
import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../../../components/Toast';
import { apiClient } from '../../../api/apiClient';
import QuickItemChips from '../../../components/QuickItemChips';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { useOverlayClickClose } from '../../../hooks/useOverlayClickClose';
import { OrderItemsList } from './OrderItemsList';
import { Field } from './utils';
import { useOrderForm, orderPayload } from './useOrderForm';
import { usePromoApi } from './promoApi';
import { ContractCompanyPicker } from '../contractmanagement/ContractCompanyPicker';
import {
  overlayStyle, modalBoxStyle, modalHeaderStyle, modalHeaderIconBox, modalHeaderTitleStyle, closeBtnStyle,
  modalBodyStyle, modalFooterStyle, sectionCard, sectionTitle, twoCol,
  inputStyle, cancelBtnStyle, savePurpleBtnStyle,
} from './styles';

// pickCompanies: 통합 화면 새 주문 때 고를 회사들(5-6, 서버가 메뉴 회사인지 다시 본다). 없으면 고르지 않는다.
export function OrderModal({ open, initialData, promotionalItemsList, categoriesList, onClose, onSaved, pickCompanies = null }) {
  const { showToast } = useToast();
  const api = usePromoApi(); // 주소 묶음(promoApi.js, 2026-10-19)
  useBodyScrollLock(open);
  const overlayCloseProps = useOverlayClickClose(onClose);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initialData?.id);
  const needPick = !isEdit && Boolean(api.pickCompany) && Array.isArray(pickCompanies) && pickCompanies.length > 0;
  const [pickedCompany, setPickedCompany] = useState('');
  const [pickError, setPickError] = useState(false);
  useEffect(() => { if (open) { setPickedCompany(''); setPickError(false); } }, [open]);
  // 입력 상태·품목 다루기는 useOrderForm.js(2026-10-19 떼어냄)
  const {
    form, setField, autoFocusNew, setAutoFocusNew,
    addItem, quickAddItem, setItem, selectItem, removeItem,
  } = useOrderForm({ open, isEdit, initialData });

  // 빠른 추가 칩 패널 — 모달 본문 맨 아래 스크롤 시 나타남
  const [chipsVisible, setChipsVisible] = useState(false);
  const bodyRef = useRef(null);

  // 모달 열릴 때 칩 상태 초기화 + 스크롤 불필요한 경우 즉시 노출
  useEffect(() => {
    if (!open) return;
    setChipsVisible(false);
    // 짧은 딜레이 후 스크롤 필요 없는 경우 자동 노출 (콘텐츠가 뷰포트보다 짧을 때)
    const t = setTimeout(() => {
      const el = bodyRef.current;
      if (el && el.scrollHeight <= el.clientHeight + 4) setChipsVisible(true);
    }, 250);
    return () => clearTimeout(t);
  }, [open]);

  async function handleSave() {
    if (!form.received_date)                          { showToast('접수일자를 입력해 주세요.', 'error'); return; }
    if (!form.category_id)                            { showToast('카테고리를 선택해 주세요.', 'error'); return; }
    if (!String(form.sales_person_name || '').trim()) { showToast('마케팅 담당자를 입력해 주세요.', 'error'); return; }
    if (!String(form.address || '').trim())           { showToast('받는분 주소를 입력해 주세요.', 'error'); return; }
    if (!String(form.mobile_phone || '').trim())      { showToast('휴대폰을 입력해 주세요.', 'error'); return; }
    const validItems = form.items.filter((i) => i.item_name && Number(i.quantity) > 0);
    if (!validItems.length) { showToast('홍보물을 1개 이상 추가해 주세요.', 'error'); return; }
    setSaving(true);
    if (needPick && !pickedCompany) { setPickError(true); setSaving(false); showToast('주문할 회사를 골라 주세요.', 'error'); return; }
    const payload = needPick ? { ...orderPayload(form), promo_company_code: pickedCompany } : orderPayload(form);
    let res;
    if (isEdit) {
      res = await apiClient.put(`${api.orders}/${initialData.id}`, payload);
    } else {
      res = await apiClient.post(api.orders, payload);
    }
    setSaving(false);
    if (!res.ok) { showToast(res.data?.message || '저장 실패', 'error'); return; }
    showToast(isEdit ? '수정되었습니다.' : '등록되었습니다.', 'success');
    onSaved();
    onClose();
  }

  if (!open) return null;
  const totalAmount = form.items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);
  // 모바일에서는 칩 패널 미노출 (화면 너비 부족)
  const canShowChips = window.innerWidth > 900;
  // 이미 추가된 item_id 집합 — 칩 비활성화 판단에 사용
  const addedItemIds = form.items.map((i) => i.item_id).filter(Boolean);

  return (
    <div style={overlayStyle} {...overlayCloseProps}>
      {/* 모달 카드 — 원래 위치 그대로 (칩 패널과 무관하게 항상 중앙) */}
      <div style={modalBoxStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={modalHeaderIconBox}>{isEdit ? '✏️' : '📦'}</div>
            <span style={modalHeaderTitleStyle}>{isEdit ? '홍보물 주문 수정' : '홍보물 주문 신규 등록'}</span>
          </div>
          <button style={closeBtnStyle} onClick={onClose}>✕</button>
        </div>
        <div
          ref={bodyRef}
          style={modalBodyStyle}
          onScroll={(e) => {
            // 맨 아래 닿으면 칩 패널 노출
            const el = e.currentTarget;
            if (!chipsVisible && el.scrollTop + el.clientHeight >= el.scrollHeight - 6) {
              setChipsVisible(true);
            }
          }}
        >
          {needPick && (
            <ContractCompanyPicker companies={pickCompanies} value={pickedCompany} error={pickError}
              onChange={(c) => { setPickedCompany(c); setPickError(false); }}
              label="주문할 회사" doneNote="고른 회사의 홍보물 주문으로 저장됩니다." needNote="회사를 골라야 등록할 수 있습니다." />
          )}
          {/* 기본 정보 */}
          <div style={sectionCard}>
            <div style={sectionTitle}>기본 정보</div>
            <div style={twoCol}>
              <Field label="접수일자 *">
                <input type="date" style={inputStyle} value={form.received_date}
                  onChange={(e) => setField('received_date', e.target.value)} />
              </Field>
              <Field label="카테고리 *">
                {/* product_categories에서 동적으로 로드 — 하드코딩 없음 */}
                <select style={{ ...inputStyle, fontSize: 12 }} value={form.category_id}
                  onChange={(e) => setField('category_id', e.target.value)}>
                  <option value="">카테고리 선택</option>
                  {(categoriesList || []).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </Field>
            </div>
            <div style={twoCol}>
              <Field label="마케팅 담당자 *">
                <input style={inputStyle} placeholder="접수한 사람" value={form.sales_person_name}
                  onChange={(e) => setField('sales_person_name', e.target.value)} />
              </Field>
            </div>
          </div>
          {/* 고객 정보 */}
          <div style={sectionCard}>
            <div style={sectionTitle}>고객 정보(받는분 정보)</div>
            <Field label="상호">
              <input style={inputStyle} placeholder="상호명" value={form.company_name}
                onChange={(e) => setField('company_name', e.target.value)} />
            </Field>
            <Field label="받는분 주소 *">
              <input style={inputStyle} placeholder="받는분 주소" value={form.address}
                onChange={(e) => setField('address', e.target.value)} />
            </Field>
            <div style={twoCol}>
              <Field label="전화번호">
                <input style={inputStyle} placeholder="02-XXXX-XXXX" value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)} />
              </Field>
              <Field label="휴대폰 *">
                <input style={inputStyle} placeholder="010-XXXX-XXXX" value={form.mobile_phone}
                  onChange={(e) => setField('mobile_phone', e.target.value)} />
              </Field>
            </div>
            {/* 메모 — 세로 크기 조절 가능 */}
            <Field label="메모">
              <textarea
                style={{ ...inputStyle, resize: 'vertical', minHeight: 56, lineHeight: 1.5 }}
                value={form.memo}
                onChange={(e) => setField('memo', e.target.value)}
                placeholder="주문 관련 메모 (선택)"
                rows={2}
              />
            </Field>
          </div>
          {/* 홍보물 명세 — OrderItemsList 컴포넌트로 분리 */}
          <OrderItemsList
            items={form.items}
            totalAmount={totalAmount}
            promotionalItemsList={promotionalItemsList}
            autoFocusNew={autoFocusNew}
            onAutoFocused={() => setAutoFocusNew(false)}
            onAddItem={addItem}
            onSetItem={setItem}
            onSelectItem={selectItem}
            onRemoveItem={removeItem}
          />
        </div>
        <div style={modalFooterStyle}>
          <button style={cancelBtnStyle} onClick={onClose}>취소</button>
          <button style={savePurpleBtnStyle} disabled={saving} onClick={handleSave}>
            {saving ? '저장 중...' : isEdit ? '변경 저장' : '등록'}
          </button>
        </div>
      </div>
      {/* 빠른 추가 칩 패널 — position:fixed로 모달 오른쪽 아래에 독립 배치
          모달 너비(maxWidth 640) 기준: 50% + 320px + 16px gap
          모달 위치와 무관하게 항상 같은 자리 → 모달이 움직이지 않음 */}
      {chipsVisible && canShowChips && (
        <div
          style={{ position: 'fixed', left: 'calc(50% + 336px)', bottom: 'calc(5vh + 60px)', zIndex: 2001 }}
          onClick={(e) => e.stopPropagation()}
        >
          <QuickItemChips
            items={promotionalItemsList}
            disabledIds={addedItemIds}
            onSelect={quickAddItem}
          />
        </div>
      )}
    </div>
  );
}
